import { Component, Inject, PLATFORM_ID, type OnInit } from '@angular/core';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../shared/services/util.service';
import { NotificationComponent } from '../../shared/components/notification/notification.component';
import { SidePanelComponent } from '../../shared/components/side-panel/side-panel.component';
import { CurrencyPipe, DatePipe, isPlatformBrowser } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../shared/services/storage.service';
import { StorageEnum } from '../../shared/enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AccountDetail } from '../../shared/models/account-detail';
import { type UserProfile } from '../../shared/models/user-profile';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from '../../shared/services/notifications.service';
import { NotificationType } from '../../shared/enums/notificationtype.enum';
import { Nav } from '../../shared/enums/nav.enum';
import { MatDividerModule } from '@angular/material/divider';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../shared/services/cms.service';
import { catchError, EMPTY, map, mergeMap } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { AccountService } from '../../shared/services/account.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { ErrorService } from '../../shared/services/error.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Router } from '@angular/router';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { FormBuilder, type FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SuccessData } from '../../shared/models/success-data.model';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { PaymentService } from '../../shared/services/payment.service';
import { MatError } from '@angular/material/form-field';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PageHeader } from '../../shared/models/page-header.model';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { SeoService } from '../../shared/services/seo.service';

@Component({
  selector: 'app-pay-plan',
  imports: [
    NotificationComponent, //
    SidePanelComponent,
    CurrencyPipe,
    DatePipe,
    MatCheckboxModule,
    MatButtonModule,
    MatDividerModule,
    ReactiveFormsModule,
    MatError,
    PageHeaderComponent,
  ],
  templateUrl: './pay-plan.component.html',
  styleUrl: './pay-plan.component.scss',
  providers: [CurrencyPipe, DatePipe],
})
export class PayPlanComponent implements OnInit {
  pageReady = false;
  pageLabels: any;
  accountDetail: AccountDetail;
  loginRes: UserProfile;
  isBrowser = false;
  panelData: any;
  payplanForm: FormGroup;
  successData = new SuccessData();
  showInlineError = false;
  headerData = new PageHeader();

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
    private readonly account: AccountService,
    private readonly cms: CmsService,
    private readonly currency: CurrencyPipe,
    private readonly date: DatePipe,
    private readonly error: ErrorService,
    private readonly fb: FormBuilder,
    private readonly notification: NotificationsService,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly utils: UtilService,
    private readonly payment: PaymentService,
    private readonly seoS: SeoService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.accountDetail = this.storage.getSession(StorageEnum.AccountDetail);
    this.loginRes = this.storage.getSession(StorageEnum.LoginResponse);
    this.payplanForm = this.fb.group({
      terms: [false, [Validators.requiredTrue.bind(this)]],
    });
  }

  ngOnInit(): void {
    if (this.router.url.includes(Nav.PayPlan) && !this.accountDetail.IsEligibleForPayPlan) {
      void this.router.navigateByUrl(Nav.AccountSummary);
    }
    this.cms
      .getContent('oas-pay-plan', 'Pay Plan')
      .pipe(
        map((data) => {
          this.pageLabels = data;
          this.headerData.Message = this.pageLabels.header;
          this.panelData = this.cms.getDataFromBuilderReference(this.pageLabels.sidePanel);
          this.seoS.setSeoTags(data.seo);
        }),
        mergeMap(() => {
          if (this.isBrowser) {
            const remainingBalance =
              (this.accountDetail.CurrentBalance ?? 0) - (this.accountDetail.PastDueBalance ?? 0);
            const showRemainingNotification =
              this.accountDetail.NextBillDueDate &&
              (this.accountDetail.PastDueBalance ?? 0) > 0 &&
              remainingBalance > 0;

            if (showRemainingNotification) {
              this.pageLabels.remainingBalanceAlert = this.pageLabels.remainingBalanceAlert.replace(
                '{RemainingBalance}',
                this.currency.transform(remainingBalance) ?? '',
              );
              this.pageLabels.remainingBalanceAlert = this.pageLabels.remainingBalanceAlert.replace(
                '{DueDate}',
                this.date.transform(this.accountDetail.NextBillDueDate, 'EEEE, MMM dd, yyyy') ?? '',
              );
              this.notification.addNotification(
                this.pageLabels.remainingBalanceAlert,
                NotificationType.Prenotification,
                Nav.PayPlan,
              );
              this.successData.PostNotificationList.push({
                Message: this.pageLabels.remainingBalanceAlert,
                NotificationType: NotificationType.Alert,
              });
            }
            return this.account.getPayPlanQuote(this.accountDetail.AccountNumber);
          } else {
            return EMPTY;
          }
        }),
      )
      .subscribe((data) => {
        const ppInfo = data.PerInstallments[0];
        this.accountDetail.PayPlanDueDate = ppInfo.PPDueDate;
        this.accountDetail.PayPlanAmount = ppInfo.PPInstallmentAmount;
        this.storage.setSession(StorageEnum.AccountDetail, this.accountDetail);

        this.pageReady = true;
        this.utils.hideSpinner();
      });
  }

  submitPayPlan(): void {
    this.utils.showSpinner();
    this.notification.clearAllNotifications();
    this.showInlineError = false;
    if (this.payplanForm.valid) {
      const data = {
        AccountId: this.accountDetail.AccountNumber,
        PayPlanRequest: [
          {
            PayPlanInstallmentAmount: this.accountDetail.PayPlanAmount,
            PayPlanInstallmentDate: this.accountDetail.PayPlanDueDate,
          },
        ],
      };
      this.account
        .payPlanEnroll(data)
        .pipe(
          catchError((err) => {
            this.utils.consoleGroup('PayPlan Enroll Error', err);
            this.error.setErrorData('none', this.pageLabels.saveError);
            void this.router.navigateByUrl(Nav.ErrorPage);
            return EMPTY;
          }),
        )
        .subscribe((data) => {
          // Update Account Detail with Pay Plan Info
          this.accountDetail.IsPayPlan = true;
          this.accountDetail.PayPlanType = data.PayPlanType;
          this.accountDetail.PayPlanDueDate = data.PayPlanDueDate;
          this.accountDetail.PayPlanAmount = data.PayPlanAmount;
          this.accountDetail.PaymentPlans = data.PaymentPlans;
          this.accountDetail.PastDueBalance = 0; // Bug#94126
          this.accountDetail.MinimumPayment = 0; // Bug#96505
          this.storage.setSession(StorageEnum.AccountDetail, this.accountDetail);
          this.setSuccessData();
          void this.router.navigateByUrl(Nav.PayPlanSuccess);
        });
    } else {
      this.showInlineError = true;
      const errorMessage = this.storage.getSession(StorageEnum.GlobalVars).formError;
      this.notification.addNotification(errorMessage, NotificationType.Alert, Nav.PayPlan);
      this.utils.hideSpinner();
    }
  }

  setSuccessData(): void {
    // post notification list set in onInit
    const succLabels = this.cms.getDataFromBuilderReference(this.pageLabels.enrollSuccess);
    const canSchedulePayment = this.payment.isEligibleForFuturePayment(this.accountDetail);

    this.successData.Header = succLabels.header;
    this.successData.Details.push({
      Label: succLabels.details[0].detailLabel,
      Value: this.currency.transform(this.accountDetail.PayPlanAmount) ?? '',
    });
    this.successData.Details.push({
      Label: succLabels.details[1].detailLabel,
      Value: this.date.transform(this.accountDetail.PayPlanDueDate, 'EEEE, MMM dd, yyyy') ?? '',
    });
    this.successData.DetailPostMessage = succLabels.detailPostMessage;
    let ctaLabels = succLabels.ctaList[0]; // Schedule Payment Button
    if (!canSchedulePayment) {
      ctaLabels = succLabels.ctaList[1]; // Account Summary button
    }
    this.successData.CTAList.push({
      Label: ctaLabels.ctaLabel,
      Link: ctaLabels.ctaUrl,
      AriaLabel: ctaLabels.ctaAriaLabel,
    });

    this.storage.setSession(StorageEnum.SuccessData, this.successData);
  }
}
